import { AdaptiveItemProps, MainMenuLogo } from '@epam/uui-components';
import { taskRoutes } from '../../../router/taskRoutes';
import { broadcastRoutes } from '../../../router/broadcastRoutes';
import logoImage from 'assets/icons/logo.svg';
import CustomMenuItem from './CustomMenuItem';
import { REVOKED_STATUS, User } from 'models/user.model';
import { NavProfile } from '../NavProfile';
import React from 'react';
import { settingsRoutes } from 'router/settingsRoutes';
import type { FeatureName } from 'models/feature-toggling.model';

type MenuItemsProps = AdaptiveItemProps<{ caption?: string }>;

const getCommonItems = (
  isLoading: boolean,
  onNavClick: (link: string) => void,
): MenuItemsProps[] => [
  {
    id: 'logo',
    priority: 99,
    render: (p) => (
      <CustomMenuItem
        key={p.id}
        isLoading={isLoading}
        link="/home"
        onNavClick={onNavClick}>
        <MainMenuLogo logoUrl={logoImage} />
      </CustomMenuItem>
    ),
  },
];

const getUserAccessItems = (user: User): MenuItemsProps[] => [
  {
    id: 'avatar',
    priority: 5,
    render: () => (
      <NavProfile
        key="profile"
        avatar={user.avatar}
        isRevoked={user.status === REVOKED_STATUS}
        role={user.role}
      />
    ),
  },
];

type IsFeatureEnabled = (feature: FeatureName) => boolean;

interface MainMenuItem {
  id: string;
  priority: number;
  caption: string;
  link: string;
  feature: FeatureName;
}

const MAIN_MENU_ITEMS: MainMenuItem[] = [
  {
    id: 'task-management',
    priority: 1,
    caption: 'Task Management',
    link: taskRoutes.base,
    feature: 'taskManagement',
  },
  {
    id: 'broadcasts',
    priority: 2,
    caption: 'Broadcasts',
    link: broadcastRoutes.base,
    feature: 'broadcasts',
  },
  {
    id: 'statistics',
    priority: 3,
    caption: 'Statistics',
    link: '/statistics',
    feature: 'statistics',
  },
  {
    id: 'settings',
    priority: 4,
    caption: 'Settings',
    link: settingsRoutes.base,
    feature: 'settings',
  },
];

const getAdminAccessItems = (
  isLoading: boolean,
  onNavClick: (link: string) => void,
  user: User,
  isFeatureEnabled: IsFeatureEnabled,
): MenuItemsProps[] => {
  const featureItems = MAIN_MENU_ITEMS.filter((item) =>
    isFeatureEnabled(item.feature),
  );

  return [
    ...getCommonItems(isLoading, onNavClick),
    ...featureItems.map<MenuItemsProps>((item) => ({
      id: item.id,
      priority: item.priority,
      caption: item.caption,
      render: (p) => (
        <CustomMenuItem
          key={p.id}
          caption={item.caption}
          isLoading={isLoading}
          link={item.link}
          onNavClick={onNavClick}
        />
      ),
    })),
    ...getUserAccessItems(user),
  ];
};

export { getCommonItems, getUserAccessItems, getAdminAccessItems };
